import React from 'react';
import classnames from 'classnames';
import type { Theme } from '@material-ui/core';
import type { TypographyProps } from '@material-ui/core/Typography';
import type { KlpStyled } from '@klp/design/Theme';
import { Typography } from '../Typography';
import { IconScale } from './IconScale';
import type { IconSize } from './IconSize';
import type { IconName, PictogramNameType } from './assets/IconNames';
import SvgUrlIcons from './assets/klp-icons.svg';
import SvgUrlPictograms from './assets/klp-pictograms.svg';
import { useKlpStyles } from '../Theme/useKlpStyles';

interface IOwnProps {
    size?: IconSize;
    icon?: IconName;
    pictogram?: PictogramNameType;
    disabled?: boolean;
}

const styles = (props: IllustrationProps) => (theme: Theme) => ({
    svgWrapper: {
        width: IconScale[props.size || 'base'].width,
        height: IconScale[props.size || 'base'].height,
        display: 'inline-block',
        whiteSpace: 'nowrap',
        wordWrap: 'normal',
    },
    svg: {
        maxWidth: '100%',
        maxHeight: '100%',
        width: '100%',
        height: '100%',
    },
    svgEnabled: {
        fill: theme.palette[props.klpPalette || 'primary']![
            props.klpColor || 'main'
        ],
    },
    svgDisabled: {
        fill: theme.palette['primary']['medium']?.concat(' !important'),
    },
});

export type IllustrationProps = IOwnProps & KlpStyled & TypographyProps;

export const Illustration: React.FC<IllustrationProps> = (props) => {
    const classes = useKlpStyles(props, styles(props));

    const { icon, pictogram, disabled, className, ...restProps } = props;

    if (!icon && !pictogram) return null;

    const svgUrl = icon ? SvgUrlIcons : SvgUrlPictograms;

    const illustrationType = icon ? 'icon' : 'pictogram';

    return (
        <Typography
            data-testid="Illustration-IconWrapper"
            className={`${classes.svgWrapper} ${className}`}
            {...restProps}
        >
            <svg
                className={classnames(classes.svg, {
                    [classes.svgDisabled]: disabled,
                    [classes.svgEnabled]: !disabled,
                })}
            >
                <use
                    href={`${svgUrl}#${icon || pictogram}`}
                    data-testid={`${illustrationType}-${icon || pictogram}`}
                />
            </svg>
        </Typography>
    );
};
